import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ZkTInputStatus } from '@zenklub/ng-ui/component/input/input-status/input-status.interface';
import { IProfessionalProfile } from 'app/interfaces/professionalprofile.interface';
import firebase from 'firebase';
const messages = require('./messages.json');

@Component({
	selector: 'app-change-password-form',
	templateUrl: 'change-password-form.component.html',
})
export class ChangePasswordFormComponent implements OnChanges {
	@Input() user: IProfessionalProfile;
	@Input() save!: boolean;
	@Output() saveChange = new EventEmitter<boolean>();
	@Output() saved = new EventEmitter<boolean>();

	form: FormGroup = new FormGroup({
		currentPassword: new FormControl('', [
			Validators.required,
			() => {
				const [error, value] =
					Object.entries(this.currentPasswordError || {})[0] || [];
				const response = {};
				if (error) response[error] = value;
				return error ? response : null;
			},
		]),
		newPassword: new FormControl('', [
			Validators.required,
			Validators.minLength(6),
			({ value }) => {
				return !value.match(/^(?=.*[A-Z])/)
					? { requiresACapitalLetter: true }
					: null;
			},
			({ value }) => {
				return !value.match(/^(?=.*[!@#\$%\^&\*])/)
					? { requiresASpecialCharacter: true }
					: null;
			},
			({ value }) => {
				return value === this.form?.value?.currentPassword
					? { sameAsCurrent: true }
					: null;
			},
		]),
		confirmNewPassword: new FormControl('', [
			Validators.required,
			({ value }) => {
				return value !== this.form?.value?.newPassword
					? { notEqual: true }
					: null;
			},
		]),
	});

	currentPasswordError = {};

	constructor(
		public angularFireAuth: AngularFireAuth,
		public notificationService: ZkNotificationService,
		public translateService: TranslateService
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.save?.currentValue) {
			this.submit();
		}
	}

	getStatus(formControlName: string): ZkTInputStatus {
		const { errors, dirty, touched } = this.form.get(formControlName);
		return (dirty || touched) && errors && Object.keys(errors)
			? 'error'
			: 'default';
	}

	getErrorMessage(formControlName: string): string {
		const { errors, dirty, touched } = this.form.get(formControlName);
		if (!(dirty || touched) || !errors) return '';
		const error = Object.entries(errors)[0];
		return messages[formControlName][error[0]] || '';
	}

	async submit(event?) {
		event?.preventDefault();
		event?.stopPropagation();

		this.form.markAllAsTouched();
		if (!this.form.valid) {
			this.saveChange.emit(false);
			return;
		}
		const { email } = this.user;
		const { currentPassword, newPassword } = this.form.value;
		const credential = firebase.auth.EmailAuthProvider.credential(
			email,
			currentPassword
		);
		const currentUser = await this.angularFireAuth.currentUser;
		currentUser
			.reauthenticateWithCredential(credential)
			.then(() => currentUser.updatePassword(newPassword))
			.then(() => this.saved.emit(true))
			.catch((error: any) => this.handleError(error))
			.finally(() => this.saveChange.emit(false));
	}

	handleError(error: any): void {
		if (error?.code) {
			const control = this.form.get('currentPassword');
			const msg = messages['currentPassword'][error.code];
			const e = {};
			e[error.code] = msg;
			control.setErrors(e);
			this.currentPasswordError = e;
			setTimeout(() => (this.currentPasswordError = {}), 1000);
		}
	}
}
